import { html } from '../lib/html.js';
import { isValidCpf, maskCpf, maskPhone, onlyDigits } from '../lib/masks.js';
import { applyApiFieldErrors, autoClearErrors, busy, confirm, setFieldError, toastError, valueOf } from '../lib/ui.js';
import { me } from '../services/me.js';
import { session } from '../services/session.js';
import { flow } from '../lib/flow.js';
import { Page, go } from './base.js';

/**
 * Primeiro acesso em 2 passos:
 *   1. Seus dados: nome (já vem do Google, quando entrou por lá) + CPF (+ celular). O e-mail vem do login.
 *   2. O que você quer fazer? Adicionar veículo, cadastrar oficina ou explorar oficinas.
 */
class CompleteProfilePage extends Page {
  template() {
    return html`
      <ion-header translucent>
        <ion-toolbar>
          <ion-title>Bem-vindo ao TicketCar</ion-title>
          <ion-buttons slot="end"><ion-button class="logout">Sair</ion-button></ion-buttons>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding" fullscreen>
        <div class="narrow step-1">
          <p class="steps"><span class="on"></span><span></span> Passo 1 de 2 · Seus dados</p>
          <p class="hint">
            Precisamos do seu CPF para validar a propriedade dos veículos. Ele não pode ser alterado depois.
          </p>
          <form novalidate>
            <ion-list lines="none" class="form">
              <ion-input label="E-mail" label-placement="stacked" fill="outline" class="email" readonly></ion-input>
              <ion-input name="name" label="Nome completo" label-placement="floating" fill="outline"
                autocomplete="name" autocapitalize="words"></ion-input>
              <ion-input name="cpf" label="CPF" label-placement="floating" fill="outline" inputmode="numeric"
                maxlength="14" placeholder="000.000.000-00"></ion-input>
              <ion-input name="phone" label="Celular (opcional)" label-placement="floating" fill="outline"
                inputmode="tel" autocomplete="tel" maxlength="15"></ion-input>
            </ion-list>
            <ion-button type="submit" expand="block" class="submit">Continuar</ion-button>
          </form>
        </div>
        <div class="narrow step-2" hidden>
          <p class="steps"><span class="on"></span><span class="on"></span> Passo 2 de 2</p>
          <h2 class="onboard-title">O que você quer fazer primeiro?</h2>
          <p class="hint">Você pode fazer tudo isso depois, pelo menu.</p>
          <div class="onboard-choices">
            <button type="button" class="onboard-choice" data-to="/vehicles/new">
              <ion-icon name="car-sport-outline"></ion-icon>
              <span><b>Adicionar meu veículo</b><small>Guarde o histórico de manutenção e receba lembretes</small></span>
              <ion-icon name="chevron-forward" class="chev"></ion-icon>
            </button>
            <button type="button" class="onboard-choice" data-to="/empresa/cadastrar">
              <ion-icon name="construct-outline"></ion-icon>
              <span><b>Cadastrar minha empresa</b><small>Registre serviços e apareça para motoristas da região</small></span>
              <ion-icon name="chevron-forward" class="chev"></ion-icon>
            </button>
            <button type="button" class="onboard-choice" data-to="/oficinas">
              <ion-icon name="location-outline"></ion-icon>
              <span><b>Encontrar uma empresa</b><small>Veja empresas perto de você, com avaliações de clientes reais</small></span>
              <ion-icon name="chevron-forward" class="chev"></ion-icon>
            </button>
          </div>
          <ion-button fill="clear" expand="block" data-to="/home">Ir para o início</ion-button>
        </div>
      </ion-content>`;
  }

  setup() {
    autoClearErrors(this);
    const cpf = this.$('ion-input[name="cpf"]');
    cpf.addEventListener('ionInput', () => (cpf.value = maskCpf(cpf.value)));
    const phone = this.$('ion-input[name="phone"]');
    phone.addEventListener('ionInput', () => (phone.value = maskPhone(phone.value)));
    this.$('form').addEventListener('submit', (e) => {
      e.preventDefault();
      this.submit();
    });
    this.querySelectorAll('[data-to]').forEach((b) => b.addEventListener('click', () => go(b.dataset.to, 'root')));
    this.$('.logout').addEventListener('click', async () => {
      await session.signOut();
      go('/login', 'root');
    });
  }

  onEnter() {
    this.$('.email').value = session.email ?? '';
    const name = this.$('ion-input[name="name"]');
    if (!name.value && session.name) name.value = session.name;
  }

  /** Cadastro salvo: mostra as opções do passo 2. */
  showChoices() {
    this.$('.step-1').hidden = true;
    this.$('.step-2').hidden = false;
    this.$('.logout').style.display = 'none';
    this.$('ion-content').scrollToTop?.();
  }

  async submit() {
    const name = valueOf(this, 'name');
    const cpf = onlyDigits(valueOf(this, 'cpf'));
    const phone = valueOf(this, 'phone');

    let ok = true;
    if (name.length < 3) (ok = false), setFieldError(this.$('ion-input[name="name"]'), 'Informe seu nome completo');
    if (!isValidCpf(cpf)) (ok = false), setFieldError(this.$('ion-input[name="cpf"]'), 'CPF inválido');
    if (phone && onlyDigits(phone).length < 10) (ok = false), setFieldError(this.$('ion-input[name="phone"]'), 'Celular inválido');
    if (!ok) return;

    const sure = await confirm({
      header: 'Confirme seu CPF',
      message: `CPF ${maskCpf(cpf)}. Ele não poderá ser alterado depois.`,
      confirmText: 'Está correto',
      cancelText: 'Corrigir',
    });
    if (!sure) return;

    await busy(this.$('.submit'), async () => {
      try {
        await me.completeProfile({ name, cpf, ...(phone ? { phone } : {}) });
        // convite aberto antes do cadastro: segue direto para ele
        if (flow.invite) return go('/', 'root');
        this.showChoices();
      } catch (err) {
        if (err.code === 'CPF_IN_USE') return setFieldError(this.$('ion-input[name="cpf"]'), 'Este CPF já está cadastrado em outra conta');
        if (err.code === 'PROFILE_EXISTS') {
          await me.load({ force: true });
          return go('/home', 'root');
        }
        if (!applyApiFieldErrors(this, err.details)) toastError(err.message);
      }
    });
  }
}

customElements.define('page-complete-profile', CompleteProfilePage);
